import type {
  CoordeCapacityLimitedBy,
  CoordeSeasonCapacity,
  Season,
} from '@cortex/domain-ac-mens-hub-api';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useCoordeCapacity } from '../hooks/useCoordeCapacity.js';

/**
 * これを下回ったら「素材づくりに使える在庫が足りない」と扱う本数。
 * 1週間分の配信素材（目安5本）を、同じ服の重複なく組めるかどうかを境界にしている。
 * @graph-connects none
 */
export const THIN_STOCK_THRESHOLD = 10;

/**
 * 季節の表示順。phoenix-config constants.json の SUB_SEASON と同じ並び。
 * @graph-connects none
 */
const SEASON_ORDER: readonly Season[] = [
  'earlySpring',
  'lateSpring',
  'summer',
  'earlyAutumn',
  'lateAutumn',
  'winter',
] as const;

/**
 * 生成上限を決めている制約の i18n キー。Record で網羅性を型で担保する。
 * @graph-connects none
 */
const LIMITED_BY_KEY: Record<CoordeCapacityLimitedBy, string> = {
  tops: 'coorde.limitedBy.tops',
  bottoms: 'coorde.limitedBy.bottoms',
  dailyLimit: 'coorde.limitedBy.dailyLimit',
};

/**
 * 生成に使える在庫が薄いか判定する。
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Flags a season whose generation ceiling is too low to build a week of distribution material without repeating garments.
 * @graph-connects none
 */
export function isThinStock(maxOutfits: number): boolean {
  return maxOutfits < THIN_STOCK_THRESHOLD;
}

/**
 * 季節を SUB_SEASON の並びに揃える。API が順序を変えても画面は常に同じ並びで比較できる。
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Orders the seasons the way the business reads them, independent of API ordering.
 * @graph-connects none
 */
export function sortSeasons(seasons: readonly CoordeSeasonCapacity[]): CoordeSeasonCapacity[] {
  return [...seasons].sort(
    (a, b) => SEASON_ORDER.indexOf(a.season) - SEASON_ORDER.indexOf(b.season),
  );
}

/**
 * 在庫が薄い季節だけを取り出す。仕入担当へ共有すべき対象そのもの。
 * @graph-node {Function}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Extracts the seasons that need a buying decision, which is the action this screen exists to trigger.
 * @graph-connects none
 */
export function selectThinSeasons(
  seasons: readonly CoordeSeasonCapacity[],
): CoordeSeasonCapacity[] {
  return sortSeasons(seasons).filter((season) => isThinStock(season.maxOutfits));
}

/**
 * @graph-node {Page}
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business Shows, per season, how many FKUs are usable for outfit generation and how many outfits that allows, so marketing sees the ceiling before requesting a run and merchandising sees which seasons need buying.
 * @graph-connects useCoordeCapacity [calls] 季節別の生成キャパシティ取得
 */
export function CoordeCapacityPage() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useCoordeCapacity();

  const seasons = useMemo(() => sortSeasons(data?.seasons ?? []), [data]);
  const thinSeasons = useMemo(() => selectThinSeasons(data?.seasons ?? []), [data]);

  if (isLoading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-[var(--color-text-muted)]">
        {t('app.loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-[var(--color-error)]">
        {t('common.fetchError')}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[20px] font-semibold tracking-tight text-[var(--color-text-primary)]">
          {t('coorde.title')}
        </h1>
        <p className="mt-1 text-[13px] text-[var(--color-text-muted)]">{t('coorde.description')}</p>
      </div>

      <dl className="grid grid-cols-2 gap-px overflow-hidden rounded-md border border-[var(--color-border)] bg-[var(--color-border)]">
        <div className="bg-[var(--color-surface)] p-3">
          <dt className="text-[12px] text-[var(--color-text-muted)]">{t('coorde.rule.reuse')}</dt>
          <dd className="text-[22px] font-semibold tabular-nums text-[var(--color-text-primary)]">
            {data?.reuseLimit ?? 0}
            <span className="ml-1 text-[12px] font-normal text-[var(--color-text-muted)]">
              {t('coorde.unit.times')}
            </span>
          </dd>
        </div>
        <div className="bg-[var(--color-surface)] p-3">
          <dt className="text-[12px] text-[var(--color-text-muted)]">{t('coorde.rule.daily')}</dt>
          <dd className="text-[22px] font-semibold tabular-nums text-[var(--color-text-primary)]">
            {data?.dailyLimit ?? 0}
            <span className="ml-1 text-[12px] font-normal text-[var(--color-text-muted)]">
              {t('coorde.unit.outfits')}
            </span>
          </dd>
        </div>
      </dl>

      {thinSeasons.length > 0 && (
        <section className="rounded-md border border-[var(--color-warning)] bg-[var(--color-warning-muted)] p-4">
          <h2 className="text-[13px] font-semibold text-[var(--color-warning)]">
            {t('coorde.alert.title', { seasonCount: thinSeasons.length })}
          </h2>
          <ul className="mt-2 space-y-1">
            {thinSeasons.map((season) => (
              <li key={season.season} className="text-[13px] text-[var(--color-text-primary)]">
                {t('coorde.alert.line', {
                  season: t(`itemCatalog.season.${season.season}`),
                  max: season.maxOutfits,
                  tops: season.usableFku.tops,
                  bottoms: season.usableFku.bottoms,
                  outer: season.usableFku.outer,
                })}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-[12px] text-[var(--color-text-muted)]">
            {t('coorde.alert.action')}
          </p>
        </section>
      )}

      <div className="overflow-x-auto rounded-md border border-[var(--color-border)] bg-[var(--color-surface)]">
        <table className="w-full min-w-[640px] text-[13px]">
          <caption className="px-3 pt-3 text-left text-[12px] text-[var(--color-text-muted)]">
            {t('coorde.table.caption')}
          </caption>
          <thead>
            <tr className="text-[12px] text-[var(--color-text-muted)]">
              <th scope="col" className="px-3 py-2 text-left font-medium">
                {t('coorde.table.season')}
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                {t('coorde.table.tops')}
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                {t('coorde.table.bottoms')}
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                {t('coorde.table.outer')}
              </th>
              <th scope="col" className="px-3 py-2 text-right font-medium">
                {t('coorde.table.maxOutfits')}
              </th>
              <th scope="col" className="px-3 py-2 text-left font-medium">
                {t('coorde.table.limitedBy')}
              </th>
            </tr>
          </thead>
          <tbody>
            {seasons.map((season) => (
              <tr
                key={season.season}
                className="border-t border-[var(--color-border)] text-[var(--color-text-primary)]"
              >
                <th scope="row" className="px-3 py-2 text-left font-normal">
                  {t(`itemCatalog.season.${season.season}`)}
                </th>
                <td className="px-3 py-2 text-right tabular-nums">{season.usableFku.tops}</td>
                <td className="px-3 py-2 text-right tabular-nums">{season.usableFku.bottoms}</td>
                <td className="px-3 py-2 text-right tabular-nums">{season.usableFku.outer}</td>
                <td className="px-3 py-2 text-right font-semibold tabular-nums">
                  {season.maxOutfits}
                </td>
                <td className="px-3 py-2 text-left text-[var(--color-text-muted)]">
                  {isThinStock(season.maxOutfits) ? (
                    <span className="mr-2 rounded-full bg-[var(--color-error-muted)] px-2 py-0.5 text-[11px] text-[var(--color-error)]">
                      {t('coorde.table.thin')}
                    </span>
                  ) : null}
                  {t(LIMITED_BY_KEY[season.limitedBy])}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {seasons.length === 0 && (
        <p className="text-[13px] text-[var(--color-text-muted)]">{t('coorde.empty')}</p>
      )}

      <p className="text-[12px] text-[var(--color-text-muted)]">{t('coorde.note')}</p>
    </div>
  );
}
